// OVERRIDE MARKET INDICATORS — the host of the ORION (OPG)'s Override Indicators fieldset inside
// the Maven Setup (CR-20260915-001, owner vrequest 2026-09-15).
//
// "Exactly this dialogue, exactly this section, exactly the same behaviour" (owner). The fieldset
// itself is `ets-opg-override-indicators`, REUSED unchanged: the per-date record, the whole-record
// promotion on the first edit (D7), the yellow cues (this date vs some date in the range, D21),
// "Clear overrides" per date (D10), the focus freeze, the double-click takeover of a spinning box,
// the super-user read-only gate. What the OPG host does AROUND that fieldset is what this
// component does: it seeds the readings from the backend services, streams the anchored date,
// steps through the dates, and installs the trader's edits as overrides.
//
// WHAT IT OWNS AND WHAT IT DOES NOT.
//   - It owns the READINGS (what the market says, keyed by date, never `edited`) and the live
//     feed for the one date on screen.
//   - It does NOT own the overrides. `overrides` is an @Input from the Setup, which owns the map
//     and the reset rule; every edit and clear EMITS a fresh map and the Setup writes it back.
//     The reason is lifecycle: this component lives inside the section body's *ngIf and dies on
//     collapse — a map held here would be lost behind a fold.
//   - It does NOT know the date rules. `dateKeys` arrives resolved (the dates the run targets).
//     Setup owns automatic clearing/restoration against date-selector states; this component
//     only reanchors readings and reports explicit human edits/clear.
//
// READINGS: exactly as the OPG host (copilot-shared/opg-copilot.component.ts, the live feed
// section) — IV re-asked on the cache's own TTL through IvCacheService; vertical skew by
// SubscribeVerticalSkew + the VerticalSkewChangedDto push, with the MANDATORY paired
// unsubscribe (the backend ref-counts skew slots); horizontal skew from HorizontalSkewService.
// Never sampled from a panel's fields. Exactly ONE date streams at a time. REPLACE, never
// mutate, every reading object: the fieldset is OnPush and shares the reference in the duo.
//
// DUO (owner ruling #1): both sides enabled = two fieldsets, calls and puts, every box that
// side's own (CR-20260921-002, replacing D5's shared IV/HS). One side = one fieldset for that side.
//
// ES5 note: arrays + indexOf only.

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {DetectMethodChanges} from '../../utils';
import {IvCacheService} from '../../iv-cache.service';
import {MessageBusService} from '../../message-bus.service';
import {ShellClientService} from '../../shell-communication/shell-client.service';
import {HorizontalSkewService} from '../../horizontal-skew/horizontal-skew.service';
import {OptionExpirationDescriptor} from '../../shell-communication/shell-dto-protocol';
import {
    SubscribeVerticalSkew,
    UnsubscribeVerticalSkew,
    VerticalSkewChangedDto
} from '../../skew-meter/skew-meter-cqrs.model';
import {
    ALL_DATES_INDEX,
    copyOverride,
    isUniformAcrossDates,
    makeEmptyOverride,
    MarketConditionOverride,
    MarketConditionOverrides,
    OpgSide,
    OverrideChangedKeys,
    readOverrideChangedKeys
} from '../../copilot-shared/opg-copilot-domain';
import {parseDayKey} from '../../adjustment-pricing-grid/copilot/apg-copilot-questionnaire';
import {fmtMavenDate} from './maven-setup-breadcrumbs';
import {MavenParamChangeMap} from './maven-setup-param-diff';

// How often the IV box re-asks, ms — the OPG host's own figure, matched to IvCacheService's TTL.
const LIVE_IV_POLL_MS = 2000;

function describeError(e: any): string {
    if (!e) {
        return 'unknown error';
    }
    if (typeof e === 'string') {
        return e;
    }
    return String(e.message || e.Message || e);
}

@Component({
    selector: 'ets-maven-override-indicators',
    templateUrl: './maven-override-indicators.component.html',
    styleUrls: ['./maven-override-indicators.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MavenOverrideIndicatorsComponent implements OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _ivCache: IvCacheService,
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService,
        // Provided by HorizontalSkewModule, which the OPG module imports eagerly — so it sits in
        // the root injector already, the same instance the OPG host reads.
        private readonly _horizontalSkew: HorizontalSkewService
    ) {
    }

    // ---- inputs ------------------------------------------------------------------------------

    // The dates the run targets, 'yyyy-MM-dd', ascending. Compared BY CONTENT: a new array with
    // the same dates moves nothing; a different list re-anchors.
    //
    // Re-anchors to "All dates" when the list is a real range, to the single date otherwise
    // (client screencast 2026-09-17 — REPLACES D3's "re-anchors to its first date"; D3's arrow
    // rule stands, its default position does not).
    @Input()
    set dateKeys(keys: string[]) {
        const next = keys || [];
        const changed = next.join('|') !== this._dateKeys.join('|');
        this._dateKeys = next;
        if (changed) {
            this._index = next.length > 1 ? ALL_DATES_INDEX : 0;
            this.pruneReadings();
            this.refreshLiveFeed();
        }
    }

    get dateKeys(): string[] {
        return this._dateKeys;
    }

    private _dateKeys: string[] = [];

    // The Setup's map — edited dates only. Read, never written: edits go out as a new map.
    @Input() overrides: MarketConditionOverrides = {};

    // The symbol the readings resolve against. A change restarts the feed on the new symbol and
    // forgets the old symbol's readings — they would be another underlying's numbers.
    @Input()
    set underlying(value: string | null) {
        const next = value || null;
        if (next === this._underlying) {
            return;
        }
        this._underlying = next;
        this.stopLiveFeed();
        this.readingsByDate = {};
        this.refreshLiveFeed();
    }

    get underlying(): string | null {
        return this._underlying;
    }

    private _underlying: string | null = null;

    // The chain's descriptors — the DTE badge on the date label reads daysToExpiration here.
    @Input() expirations: OptionExpirationDescriptor[] = [];

    @Input() callsEnabled = false;

    @Input() putsEnabled = false;

    // Run in flight (the Setup's isRunning).
    @Input() disabled = false;

    // The Setup's changed-since-run map (owner vrequest 2026-09-15; grammar in
    // maven-setup-param-diff.ts). This host reads the anchored date's four keys and hands the
    // fieldset the per-box shape it paints from — the fieldset's own input, unchanged.
    @Input() changedKeys: MavenParamChangeMap | null = null;

    // UNION on the "All dates" position (owner ruling 2026-09-17): flag a box if ANY date in the
    // range no longer matches what the run was sent. Once an All-dates edit has stamped the range
    // this is identical to the per-date answer; it differs only when per-date edits were mixed in,
    // which is exactly when the warning is worth having. Showing nothing on the position the
    // section now OPENS in would make a changed parameter read as "nothing is wrong".
    get currentChangedKeys(): OverrideChangedKeys | null {
        const keys = this.isAllDates ? this._dateKeys : (this.currentKey ? [this.currentKey] : []);
        return readOverrideChangedKeys(this.changedKeys, keys);
    }

    // A FRESH map every time. The Setup stores it and feeds it back through `overrides`.
    @Output() overridesChange = new EventEmitter<MarketConditionOverrides>();
    // Provenance, not a comparison of per-date values: only an explicit All Dates edit is reusable.
    @Output() allDatesOverrideChange = new EventEmitter<MarketConditionOverride | null>();

    // ---- readings ----------------------------------------------------------------------------

    // What the MARKET says, keyed by date. Always `edited: false`.
    readingsByDate: MarketConditionOverrides = {};

    // ALL_DATES_INDEX (-1) is the AGGREGATE position; the clamp in currentKey makes it stream and
    // display the FIRST date, which is what that position shows until an edit stamps the range.
    private _index = ALL_DATES_INDEX;

    private _destroyed = false;

    // ---- layout ------------------------------------------------------------------------------

    get duo(): boolean {
        return this.callsEnabled && this.putsEnabled;
    }

    // The one fieldset's side outside the duo. With nothing enabled the calls layout is shown,
    // disabled — the boxes still display the market, which is worth seeing.
    get singleSide(): OpgSide {
        return (this.putsEnabled && !this.callsEnabled) ? 'puts' : 'calls';
    }

    // ---- the anchored date -------------------------------------------------------------------

    get currentKey(): string | null {
        const keys = this._dateKeys;
        if (keys.length === 0) {
            return null;
        }
        const i = Math.min(Math.max(this._index, 0), keys.length - 1);
        return keys[i];
    }

    // What the boxes show: the trader's override for this date if there is one, else the reading.
    get currentOverride(): MarketConditionOverride | null {
        const key = this.currentKey;
        if (!key) {
            return null;
        }
        const map = this.overrides || {};
        return map[key] || this.readingsByDate[key] || null;
    }

    // Frame + legend cue (D21): some date in the list carries an override.
    get rangeOverridden(): boolean {
        const map = this.overrides || {};
        for (let i = 0; i < this._dateKeys.length; i++) {
            if (map[this._dateKeys[i]]) {
                return true;
            }
        }
        return false;
    }

    // Editable-looking boxes with nothing to write to would be a lie (the fieldset's own guard
    // drops the keystroke): inert until there is a date, a side, and no run in flight.
    get inputsDisabled(): boolean {
        return this.disabled || !this.currentKey || (!this.callsEnabled && !this.putsEnabled);
    }

    // Maven's own date string ("Thu 17-Sep-26") plus the backend's DTE badge, never a raw key on
    // screen (the OPG smoke of 2026-07-22 caught exactly that leak).
    // The AGGREGATE position. Needs a real range: with one date there is nothing to standardise
    // across, so the position does not exist and writes fall back to that date.
    get isAllDates(): boolean {
        return this._index <= ALL_DATES_INDEX && this._dateKeys.length > 1;
    }

    get dateLabel(): string | null {
        if (this.isAllDates) {
            return 'All dates';
        }
        return this.dateLabelFor(this.currentKey);
    }

    // PROVENANCE (owner ruling 2026-09-17): name the date the displayed numbers came from, so an
    // aggregate label can never pass a one-date reading off as an all-dates value. Dropped only
    // once every date provably carries the same record — proof, not "the user probably edited".
    get sourceDateLabel(): string | null {
        if (!this.isAllDates) {
            return null;
        }
        if (isUniformAcrossDates(this.overrides, this._dateKeys)) {
            return null;
        }
        return this.dateLabelFor(this.currentKey);
    }

    private dateLabelFor(key: string | null): string | null {
        if (!key) {
            return null;
        }
        const d = parseDayKey(key);
        return (d ? fmtMavenDate(d) : key) + this.dteBadgeFor(key);
    }

    private dteBadgeFor(key: string): string {
        const list = this.expirations || [];
        for (let i = 0; i < list.length; i++) {
            const desc = list[i];
            if (desc && desc.optionExpirationDate === key) {
                return (typeof desc.daysToExpiration === 'number' && isFinite(desc.daysToExpiration))
                    ? ' (' + desc.daysToExpiration + 'd)'
                    : '';
            }
        }
        return '';
    }

    // Arrows only when there is more than one date to step through (D3).
    get showArrows(): boolean {
        return this._dateKeys.length > 1;
    }

    // Stepping back off the first date returns to "All dates" rather than stopping there.
    get canPrev(): boolean {
        return this._index > ALL_DATES_INDEX;
    }

    get canNext(): boolean {
        return this._index < this._dateKeys.length - 1;
    }

    @DetectMethodChanges()
    onPrev(): void {
        if (this.canPrev) {
            this._index--;
            this.refreshLiveFeed();
        }
    }

    @DetectMethodChanges()
    onNext(): void {
        if (this.canNext) {
            this._index++;
            this.refreshLiveFeed();
        }
    }

    // The fieldset emits the whole record it wants for the anchored date; this installs it as an
    // override in a NEW map and hands the map up. The reading underneath keeps ticking — the
    // override shadows it (D20), it does not stop it.
    // Which dates an edit or a clear writes to: EVERY date on the aggregate position, the anchored
    // date otherwise. One definition, so an edit and a clear cannot disagree about what "all" was.
    private writeTargets(): string[] {
        if (this.isAllDates) {
            return this._dateKeys;
        }
        const key = this.currentKey;
        return key ? [key] : [];
    }

    @DetectMethodChanges()
    onOverrideChanged(next: MarketConditionOverride): void {
        if (!next) {
            return;
        }
        const targets = this.writeTargets();
        if (targets.length === 0) {
            return;
        }
        const map = this.cloneMap(this.overrides);
        // A SEPARATE object per date. Sharing one instance across the range would alias them, so a
        // later per-date edit would silently rewrite every other date through that alias.
        for (let i = 0; i < targets.length; i++) {
            map[targets[i]] = copyOverride(next, {edited: true, ivLoading: false, vsLoading: false});
        }
        this.overridesChange.emit(map);
        if (this.isAllDates) { this.allDatesOverrideChange.emit(copyOverride(next)); }
    }

    // THIS date only (D10). The reading was never stopped, so the boxes fall straight back to the
    // market.
    @DetectMethodChanges()
    onOverrideClear(): void {
        // Same targets as an edit: on "All dates" the link clears the whole range.
        const targets = this.writeTargets();
        if (targets.length === 0) {
            return;
        }
        const map = this.cloneMap(this.overrides);
        for (let i = 0; i < targets.length; i++) {
            delete map[targets[i]];
        }
        this.overridesChange.emit(map);
        if (this.isAllDates) { this.allDatesOverrideChange.emit(null); }
    }

    private cloneMap(map: MarketConditionOverrides | null | undefined): MarketConditionOverrides {
        const out: MarketConditionOverrides = {};
        const src = map || {};
        const keys = Object.keys(src);
        for (let i = 0; i < keys.length; i++) {
            const v = src[keys[i]];
            if (v) {
                out[keys[i]] = copyOverride(v);
            }
        }
        return out;
    }

    // Readings for dates no longer targeted are dropped; the one on screen survives by definition.
    private pruneReadings(): void {
        const held = Object.keys(this.readingsByDate);
        for (let i = 0; i < held.length; i++) {
            if (this._dateKeys.indexOf(held[i]) < 0) {
                delete this.readingsByDate[held[i]];
            }
        }
    }

    // ---- live indicator feed (the OPG host's rules, verbatim) --------------------------------

    private _liveKey: string | null = null;
    private _liveIvTimer: any = null;
    private _liveVsSub: Subscription | null = null;
    private _liveHsSub: Subscription | null = null;
    private _liveVsTarget: { ul: string; exp: string } | null = null;

    // The single decision point: which date (if any) should be streaming right now.
    private refreshLiveFeed(): void {
        const key = this.currentKey;
        if (!key || !this._underlying) {
            this.stopLiveFeed();
            return;
        }
        if (this._liveKey === key) {
            return;   // already streaming this date — do not churn the subscription
        }
        this.stopLiveFeed();
        this.startLiveFeed(this._underlying, key);
    }

    private startLiveFeed(ul: string, key: string): void {
        this._liveKey = key;

        // REPLACE, never mutate: re-anchoring onto a date whose earlier fetch came back empty
        // raises the loading flags, and only a new object makes the OnPush fieldset show them.
        const prev = this.readingsByDate[key] || makeEmptyOverride();
        this.readingsByDate[key] = copyOverride(prev, {
            edited: false,
            ivLoading: prev.ivCall == null || prev.ivPut == null,
            vsLoading: prev.vsCall == null || prev.vsPut == null
        });

        // IV — query-backed; kept live by re-asking on the cache's TTL.
        this.pollLiveIv(ul, key);
        this._liveIvTimer = setInterval(() => this.pollLiveIv(ul, key), LIVE_IV_POLL_MS);

        // VS — a genuine push. Subscribing is what makes the box tick.
        this._liveVsTarget = {ul: ul, exp: key};
        try {
            this._shellClient.processCommand(new SubscribeVerticalSkew(ul, key));
        } catch (e) {
            console.log('[MavenOverrides] VS subscribe failed for ' + key + ': ' + describeError(e));
        }
        this._liveVsSub = this._messageBus.of<VerticalSkewChangedDto>('VerticalSkewChangedDto')
            .subscribe(m => {
                const dto = m ? m.payload : null;
                if (!dto || dto.underlying !== ul || dto.expiration !== key) {
                    return;   // another surface's expiration
                }
                this.writeLive(key, o => {
                    // null stays null: NO USABLE READING, never "flat" (a zero would classify
                    // Flat and quietly change what the engine returns).
                    o.vsCall = typeof dto.callSkew === 'number' ? dto.callSkew : null;
                    o.vsPut = typeof dto.putSkew === 'number' ? dto.putSkew : null;
                    o.vsLoading = !!dto.callLoading || !!dto.putLoading;
                });
            });

        // HS — push, through the service that owns the term-structure subscription: read once
        // now, then follow its change notification.
        // ONE market reading, written into BOTH sides (CR-20260921-002): the sides agree until
        // the trader edits one of them.
        this.writeLive(key, o => {
            o.hsCall = o.hsPut = this._horizontalSkew ? this._horizontalSkew.ratio : null;
        });
        if (this._horizontalSkew) {
            this._liveHsSub = this._horizontalSkew.updated$.subscribe(() =>
                this.writeLive(key, o => { o.hsCall = o.hsPut = this._horizontalSkew.ratio; }));
        }
    }

    private pollLiveIv(ul: string, key: string): void {
        this._ivCache.getIv(ul, key)
            .then(reply => this.writeLive(key, o => {
                o.ivCall = o.ivPut = (reply && typeof reply.volatility === 'number') ? reply.volatility : null;
                o.ivLoading = false;
            }))
            .catch(e => {
                // Never swallow: an empty box looks the same whether the query returned nothing,
                // failed, or was never answered.
                console.log('[MavenOverrides] IV feed failed for ' + key + ': ' + describeError(e));
                this.writeLive(key, o => { o.ivLoading = false; });
            });
    }

    private stopLiveFeed(): void {
        if (this._liveIvTimer !== null) {
            clearInterval(this._liveIvTimer);
            this._liveIvTimer = null;
        }
        if (this._liveVsSub) {
            this._liveVsSub.unsubscribe();
            this._liveVsSub = null;
        }
        if (this._liveHsSub) {
            this._liveHsSub.unsubscribe();
            this._liveHsSub = null;
        }
        if (this._liveVsTarget) {
            // MANDATORY pairing: the backend ref-counts vertical-skew slots. Cleared before the
            // call so a throw cannot leave a stale target behind.
            const target = this._liveVsTarget;
            this._liveVsTarget = null;
            try {
                this._shellClient.processCommand(new UnsubscribeVerticalSkew(target.ul, target.exp));
            } catch (e) {
                console.log('[MavenOverrides] VS unsubscribe failed: ' + describeError(e));
            }
        }
        this._liveKey = null;
    }

    // Apply one live tick to the MARKET's side only, replacing the record object.
    private writeLive(key: string, mutate: (o: MarketConditionOverride) => void): void {
        if (this._destroyed || this._liveKey !== key) {
            return;
        }
        const current = this.readingsByDate[key];
        if (!current) {
            return;
        }
        const next = copyOverride(current, {edited: false});
        mutate(next);
        this.readingsByDate[key] = next;
        this._changeDetector.detectChanges();
    }

    ngOnDestroy(): void {
        // A collapse of the section destroys this component: the paired unsubscribe here is what
        // keeps a folded section from holding a skew slot.
        this._destroyed = true;
        this.stopLiveFeed();
    }
}
